import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tapLight } from "@/lib/haptics";
import tryonHero from "@/assets/onboarding/tryon.webp";
import auraHero from "@/assets/onboarding/aura.webp";
import styleHero from "@/assets/onboarding/style.webp";
import testimonialsHero from "@/assets/onboarding/testimonials.webp";

const HEADLINE_FONT = "'Space Grotesk', sans-serif";
const BODY_FONT = "'Manrope', sans-serif";

type Slide = {
  eyebrow: string;
  title: string;
  body: string;
  image: string;
  /** Optional testimonial block rendered above the title. */
  testimonial?: {
    quote: string;
    author: string;
    rating: number;
  };
};

const SLIDES: Slide[] = [
  {
    eyebrow: "AI Rendered",
    title: "Try Any Outfit On You",
    body: "Drop a product link or photo. AI puts it on your body in seconds.",
    image: tryonHero,
  },
  {
    eyebrow: "Aura Score",
    title: "Score Your Style Aura",
    body: "Get an instant rating on fit, color harmony, and statement power.",
    image: auraHero,
  },
  {
    eyebrow: "Style Templates",
    title: "Find Your Perfect Vibe",
    body: "Indian ethnic, K-drama chic, streetwear, formal — one tap to remix.",
    image: styleHero,
  },
  {
    eyebrow: "Loved by 50,000+",
    title: "Real People, Real Glow-Ups",
    body: "Join thousands styling smarter every day with FitAura AI.",
    image: testimonialsHero,
    testimonial: {
      quote: "Skipped 3 returns last week. This thing actually works.",
      author: "Priya · Mumbai",
      rating: 5,
    },
  },
];

interface Props {
  onComplete: () => void;
}

/**
 * Native Android onboarding intro (feature tour). Shown once before the
 * profile-calibration wizard. Layout matches the Stitch design — full-bleed
 * hero image, dark gradient overlay, eyebrow chip, big title, body, and
 * sticky Next/Get Started CTA with progress dots.
 */
const NativeOnboardingIntro = ({ onComplete }: Props) => {
  const [index, setIndex] = useState(0);
  const slide = SLIDES[index];
  const isLast = index === SLIDES.length - 1;

  useEffect(() => {
    const fontId = "stitch-fonts";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;900&family=Manrope:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const next = () => {
    void tapLight();
    if (isLast) {
      onComplete();
    } else {
      setIndex((i) => i + 1);
    }
  };

  const skip = () => {
    void tapLight();
    onComplete();
  };

  return (
    <div
      className="dark relative flex min-h-[100dvh] flex-col overflow-hidden bg-[#0e0e11] text-[#e7e4ec]"
      style={{
        fontFamily: BODY_FONT,
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Full-bleed hero */}
      <div className="relative flex-1 overflow-hidden">
        <img
          key={slide.image}
          src={slide.image}
          alt=""
          className="absolute inset-0 h-full w-full object-cover animate-in fade-in duration-500"
          loading="eager"
          decoding="async"
        />
        {/* Dark gradient bottom-up + ambient glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e0e11]/40 via-[#0e0e11]/20 to-[#0e0e11]" />
        <div className="pointer-events-none absolute -left-32 top-1/4 h-[320px] w-[320px] rounded-full bg-[#ac8aff]/25 blur-[120px]" />
        <div className="pointer-events-none absolute -right-32 bottom-1/4 h-[320px] w-[320px] rounded-full bg-[#ec4899]/20 blur-[120px]" />

        {/* Top bar: brand + skip */}
        <div className="relative z-10 flex items-center justify-between px-5 pt-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#ac8aff] to-[#8f60fa] shadow-[0_0_20px_-4px_#ac8aff]">
              <Sparkles className="h-4 w-4 text-[#0e0e11]" strokeWidth={2.5} />
            </span>
            <span
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e7e4ec]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              FitAura AI
            </span>
          </div>
          {!isLast && (
            <button
              type="button"
              onClick={skip}
              className="rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#acaab1] hover:text-[#e7e4ec] active:scale-95"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              Skip
            </button>
          )}
        </div>
      </div>

      {/* Content block (overlays bottom of image area too via negative margin feel) */}
      <div className="relative z-10 px-6 pb-6 pt-2">
        {/* Eyebrow chip */}
        <div className="mb-4 flex items-center gap-3">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#bfa5ff] backdrop-blur-md"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            <Sparkles className="h-3 w-3" />
            {slide.eyebrow}
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#76757b]"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            — Step {index + 1} of {SLIDES.length}
          </span>
        </div>

        {slide.testimonial && (
          <div
            key={`quote-${index}`}
            className="mb-4 animate-in fade-in slide-in-from-bottom-2 duration-500 rounded-2xl border border-white/10 bg-white/[0.04] p-3.5 backdrop-blur-md"
          >
            <div className="mb-1.5 flex items-center gap-1">
              {Array.from({ length: slide.testimonial.rating }).map((_, i) => (
                <Star
                  key={i}
                  className="h-3.5 w-3.5 fill-[#ffd35e] text-[#ffd35e]"
                  strokeWidth={1.5}
                />
              ))}
              <span
                className="ml-2 text-[9px] font-bold uppercase tracking-[0.25em] text-[#bfa5ff]"
                style={{ fontFamily: HEADLINE_FONT }}
              >
                Verified user
              </span>
            </div>
            <div className="flex gap-2">
              <Quote className="h-3.5 w-3.5 shrink-0 text-[#ac8aff]" strokeWidth={2.5} />
              <p className="text-[13px] leading-snug text-[#e7e4ec]">
                {slide.testimonial.quote}
              </p>
            </div>
            <p
              className="mt-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#acaab1]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              — {slide.testimonial.author}
            </p>
          </div>
        )}

        <h1
          key={`title-${index}`}
          className="animate-in fade-in slide-in-from-bottom-2 duration-500 text-[28px] font-black uppercase leading-[1.05] tracking-tight text-[#e7e4ec]"
          style={{ fontFamily: HEADLINE_FONT }}
        >
          {slide.title}
        </h1>

        <p
          key={`body-${index}`}
          className="mt-3 animate-in fade-in slide-in-from-bottom-2 duration-500 text-[15px] leading-relaxed text-[#acaab1]"
        >
          {slide.body}
        </p>

        {/* Progress dots */}
        <div className="mt-6 flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index
                  ? "w-8 bg-[#ac8aff] shadow-[0_0_12px_-2px_#ac8aff]"
                  : i < index
                    ? "w-1.5 bg-[#ac8aff]/60"
                    : "w-1.5 bg-white/15"
              }`}
            />
          ))}
        </div>

        {/* CTA */}
        <Button
          type="button"
          onClick={next}
          className="mt-5 h-14 w-full rounded-2xl bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-[13px] font-bold uppercase tracking-[0.22em] text-[#0e0e11] shadow-[0_0_30px_-6px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff] active:scale-[0.98]"
          style={{ fontFamily: HEADLINE_FONT }}
        >
          {isLast ? "Get Started" : "Next"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default NativeOnboardingIntro;
